import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { CORPORATE_EVENT_FORMATS } from "@/data/corporate-event-formats";
import { useEventCategories } from "@/hooks/useEventCategories";
import { cn } from "@/lib/utils";

interface CategoryCarouselProps {
  className?: string;
}

export function CategoryCarousel({ className }: CategoryCarouselProps) {
  const { t } = useTranslation("landingHome");
  const { corporateEnabled } = useEventCategories();

  return (
    <section aria-labelledby="home-categories-heading" className={cn("py-8 md:py-12", className)}>
      <div className="container">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div>
            <h2 id="home-categories-heading" className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
              {t("categories.title")}
            </h2>
            <p className="text-muted-foreground mt-1 text-sm md:text-base">{t("categories.subtitle")}</p>
          </div>
          <Link
            to="/eventos"
            className="group flex min-h-11 shrink-0 items-center gap-1 self-start rounded-md text-sm font-semibold text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("categories.viewAll")} <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <ul className="flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain pb-3 pt-1 px-1 -mx-1">
          {CORPORATE_EVENT_FORMATS.map((category) => {
            const Icon = category.icon;
            return (
              <li key={category.id} className="w-32 shrink-0 snap-start sm:w-36">
                <Link
                  to={corporateEnabled ? `/eventos?categoria=${category.id}` : `/eventos?q=${encodeURIComponent(category.search)}`}
                  className="group flex min-h-32 h-full flex-col items-center justify-center gap-4 rounded-2xl border border-border/60 bg-card px-3 py-5 text-center text-sm font-medium text-foreground transition-colors duration-150 hover:border-primary/40 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none sm:min-h-36"
                >
                  <Icon aria-hidden="true" className="h-7 w-7 shrink-0 text-muted-foreground group-hover:text-primary" strokeWidth={1.75} />
                  <span className="max-w-full break-words">{t(`hero.slides.${category.id}.word`).replace(/^./u, (letter) => letter.toLocaleUpperCase())}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
